// @ts-strict-ignore
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import dayjs from 'dayjs';
import {
  ButtonSize,
  ButtonVariant,
  IconName, StyledBulkAction, Table, makeInitialTableState, useTableLocalStorage,
} from '@usealloy/component-library';
import { typhonRoutes } from '~/helpers/routes';
import { typhonAjaxFetch } from '~/helpers/api';
import { UserContext } from '~/dashboard/context';
import { initialFilterState } from './constants';
import { formatTransactionPubAttrs, getTransactionColumns, UNKNOWN_VALUE } from './helpers';
import { TransactionHistoryFilters } from './TransactionHistoryFilters';
import TransactionMetaModal from '../TransactionMetaModal';
import TransactionStatusUpdateModal from '../TransactionStatusUpdateModal';
import { TRX_COLUMNS } from '../constants';
import type { WebhookAuditEventDetails } from '~/investigations/redux/types';
import { EvidenceModal } from '~/views/EvidenceModal';
import { EVIDENCE_DATA_TYPES } from '~/constants';
import { useEvidencePermissions } from '~/helpers/hooks';
import type { Transaction } from '../types';
import { useQuery } from '@tanstack/react-query';

interface TransactionHistoryTableProps {
  entityToken: string;
  extEntityId?: string;
  hasEvidenceModal?: boolean;
  hasInvTransactionsWebhook?: boolean;
  investigationToken?: string | null;
  isGroupInvestigation?: boolean;
  isInvestigationPage?: boolean;
  isLoading?: boolean;
  transactions?: Transaction[];
  webhookAuditEventDetailsByTransactionId?: { [transactionId: string]: WebhookAuditEventDetails };
}

export const TransactionHistoryTable = (
  {
    entityToken,
    extEntityId,
    hasEvidenceModal = false,
    hasInvTransactionsWebhook = false,
    investigationToken = null,
    isGroupInvestigation = false,
    isInvestigationPage = false,
    isLoading: isLoadingFromProps,
    transactions: transactionsFromProps,
    webhookAuditEventDetailsByTransactionId,
  }: TransactionHistoryTableProps,
) => {
  const [filterState, setFilterState] = useState(initialFilterState);
  const [tableState, setTableState] = useState({
    ...makeInitialTableState({
      pagination: {
        pageIndex: 0,
        pageSize: isInvestigationPage ? 10 : 20,
      },
    }),
  });
  const [modalMetadata, setModalMetadata] = useState({});
  const [displayMetaModal, toggleMetaModal] = useState(false);
  const [showTrxStatusUpdateModal, setShowTrxStatusUpdateModal] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);

  const {
    getStateFromLocalStorage,
    saveStateToLocalStorage,
  } = useTableLocalStorage('entity-transaction-history-table');

  const { typhonDomain, featureFlags } = useContext(UserContext);

  // Check evidence permissions (includes feature flag check)
  const { canReadEvidence, evidenceButtonText } = useEvidencePermissions();
  const url = `${typhonDomain}${typhonRoutes.entities.transactions.getAll(entityToken)}`;
  const {
    data: transactionsFetched,
    error,
    isLoading,
  } = useQuery({
    enabled: !transactionsFromProps,
    initialData: [],
    queryKey: ['transactions', entityToken],
    queryFn: async () => {
      const { transactions } = await typhonAjaxFetch(url);
      return transactions;
    },
  });

  const formattedTransactions = useMemo(() => (
    formatTransactionPubAttrs(transactionsFromProps ?? transactionsFetched)
  ), [transactionsFromProps, transactionsFetched]);

  const loading = useMemo(() => isLoadingFromProps ?? isLoading, [isLoadingFromProps, isLoading]);

  const handleEvidenceClick = transaction => {
    setSelectedTransactions([transaction]);
    setShowEvidenceModal(true);
  };

  const columns = useMemo(() => (
    getTransactionColumns(
      webhookAuditEventDetailsByTransactionId,
      canReadEvidence,
      hasEvidenceModal ? handleEvidenceClick : undefined,
      isGroupInvestigation,
    )
  ), [
    webhookAuditEventDetailsByTransactionId,
    canReadEvidence,
    hasEvidenceModal,
    isGroupInvestigation,
  ]);

  const filteredTransactions = useMemo(() => (
    [...formattedTransactions].filter(datum => {
      let includeDatum = true;

      if (
        // FLOW
        (filterState.flow.value === 'Credit' && datum.amount < 0)
        || (filterState.flow.value === 'Debit' && datum.amount >= 0)
        // DATE RANGE
        || (
          filterState.dateRange !== null
          && (!dayjs(datum.created_at).isBetween(
            dayjs(filterState.dateRange?.[0]),
            dayjs(filterState.dateRange?.[1]),
          ))
        )
        // PROCESS METHOD
        || (
          filterState.processMethod.value !== 'All'
          && filterState.processMethod.value !== datum.process_method
        )
        // CATEGORY
        || (
          filterState.category.value !== 'All'
          && filterState.category.value !== datum.category
        )
        // PRODUCT NAME
        || (
          filterState.productName.value !== 'All'
          && filterState.productName.value !== datum.source_bank_product_name
        )
        // INTERACTION POINT
        || (
          filterState.interactionPoint.value !== 'All'
          && filterState.interactionPoint.value !== datum.interaction_point
        )
        // AMOUNT
        || (
          !filterState.amount.some(amount => amount === '')
          && !(
            parseInt(filterState.amount[0], 10) <= (datum.amount / 100)
            && (datum.amount / 100) <= parseInt(filterState.amount[1], 10)
          )
        )
      ) {
        includeDatum = false;
      }

      return includeDatum;
    })
  ), [formattedTransactions, filterState]);

  const sortedTransactions = useMemo(() => {
    const sortColumn = tableState.sorting?.[0]?.id;
    const sortDesc = tableState.sorting?.[0]?.desc;
    const activeColumn = columns.find(col => col.id === sortColumn);

    const isEmptyValue = (val: unknown) => val == null || val === UNKNOWN_VALUE;

    return [...filteredTransactions].sort((trxA, trxB) => {
      const accessor = activeColumn?.accessorFn as ((row: Transaction) => unknown) | undefined;
      const colA = accessor ? accessor(trxA) : trxA[sortColumn];
      const colB = accessor ? accessor(trxB) : trxB[sortColumn];

      const emptyA = isEmptyValue(colA);
      const emptyB = isEmptyValue(colB);
      if (emptyA && emptyB) return 0;
      if (emptyA) return 1;
      if (emptyB) return -1;

      let result: number;
      if (typeof colA === 'string' && typeof colB === 'string') {
        result = colA.localeCompare(colB, undefined, { numeric: true, sensitivity: 'base' });
      } else if (colA < colB) {
        result = -1;
      } else if (colA > colB) {
        result = 1;
      } else {
        result = 0;
      }

      return sortDesc ? -result : result;
    });
  }, [filteredTransactions, tableState.sorting, columns]);

  const pageData = useMemo(() => {
    const { pageIndex, pageSize } = tableState.pagination;
    const startIndex = pageSize * pageIndex;
    const endIndex = startIndex + pageSize;
    return sortedTransactions.slice(startIndex, endIndex);
  }, [sortedTransactions, tableState.pagination]);

  useEffect(() => {
    const selectedTrx = [];

    Object.keys(tableState.rowSelection)
      .filter(trxExtId => tableState.rowSelection[trxExtId])
      .forEach(trxExtId => {
        const transaction = pageData.find(
          trx => trx.external_transaction_identifier.toString() === trxExtId,
        );
        if (transaction) {
          selectedTrx.push(transaction);
        }
      });

    setSelectedTransactions(selectedTrx);
  }, [tableState.rowSelection, pageData]);

  const CSVColumns = useMemo(() => (
    getTransactionColumns(
      undefined,
      canReadEvidence,
      undefined,
      isGroupInvestigation,
    ).reduce((formattedColumns, { header, id }) => {
      const { columnVisibility } = tableState;
      // only omit the column if it's false in columnVisibility
      if (
        columnVisibility[id] === undefined
        || columnVisibility[id] === true
      ) {
        formattedColumns.push({
          name: header,
          prop: id,
        });
      }
      return formattedColumns;
    }, [])
  ), [tableState, canReadEvidence, isGroupInvestigation]);

  const shouldEnableRowSelection = trx => isInvestigationPage
    && hasInvTransactionsWebhook
    && (trx.status ?? '').toLowerCase().trim() === 'paused';

  const canChangeStatus = trx => shouldEnableRowSelection(trx);

  const canAddEvidence = canReadEvidence && hasEvidenceModal;

  const shouldShowChangeStatusBulkAction = useMemo(() => {
    if (selectedTransactions.length === 0) return false;
    return selectedTransactions.every(trx => (
      isInvestigationPage
      && hasInvTransactionsWebhook
      && (trx.status ?? '').toLowerCase().trim() === 'paused'
    ));
  }, [
    selectedTransactions,
    isInvestigationPage,
    hasInvTransactionsWebhook,
  ]);

  return (
    <>
      <TransactionHistoryFilters
        filterState={filterState}
        setFilterState={setFilterState}
        data={formattedTransactions}
        extEntityId={extEntityId}
        filteredTransactions={sortedTransactions}
        CSVColumns={CSVColumns}
      />
      <Table
        bulkActions={[
          {
            render: () => (
              shouldShowChangeStatusBulkAction ? (
                <StyledBulkAction
                  icon={IconName.Pencil}
                  onClick={() => {
                    setShowTrxStatusUpdateModal(true);
                  }}
                  size={ButtonSize.medium}
                  variant={ButtonVariant.tertiary}
                >
                  Change Status
                </StyledBulkAction>
              ) : null
            ),
          },
          {
            render: () => (
              canAddEvidence ? (
                <StyledBulkAction
                  icon={IconName.Paperclip}
                  onClick={() => {
                    setShowEvidenceModal(true);
                  }}
                  size={ButtonSize.medium}
                  variant={ButtonVariant.tertiary}
                >
                  {evidenceButtonText}
                </StyledBulkAction>
              ) : null
            ),
          },
        ]}
        columns={columns}
        data={pageData}
        recordCount={sortedTransactions.length}
        loading={loading}
        error={Boolean(error)}
        forceRowActionsDropdownMenu
        {...(featureFlags?.addEvidenceToTrxsAndBas && {
          rowActionsPendoAnchor: 'transaction-evidence-notes-docs',
        })}
        onTableStateChange={newState => {
          setTableState(newState);
          saveStateToLocalStorage(newState);
        }}
        state={{
          ...tableState,
          ...getStateFromLocalStorage(),
        }}
        enablePagination
        enableColumnHiding
        enableSorting
        enableColumnResizing
        rowActions={[
          {
            name: 'View Additional Data',
            tooltip: 'View Additional Data',
            onClick: ({ meta }) => {
              setModalMetadata(meta);
              toggleMetaModal(true);
            },
            shouldRender: ({ meta }) => meta && Object.keys(meta).length > 0,
            iconName: IconName.View,
          },
          {
            name: 'Change Status',
            tooltip: 'Change Status',
            onClick: row => {
              setSelectedTransactions([row]);
              setShowTrxStatusUpdateModal(true);
            },
            shouldRender: row => (
              isInvestigationPage
              && hasInvTransactionsWebhook
              && canChangeStatus(row)
            ),
          },
          {
            name: evidenceButtonText,
            tooltip: evidenceButtonText,
            onClick: row => {
              setSelectedTransactions([row]);
              setShowEvidenceModal(true);
            },
            shouldRender: () => canAddEvidence,
          },
        ]}
        enableRowSelection={row => (
          canChangeStatus(row.original) || canAddEvidence
        )}
        getRowId={row => row.external_transaction_identifier}
        customPageSizeOptions={[10, 20, 50, 100]}
      />
      <TransactionMetaModal
        isOpen={displayMetaModal}
        closeModal={() => toggleMetaModal(false)}
        transactionMeta={modalMetadata}
      />
      <TransactionStatusUpdateModal
        isOpen={showTrxStatusUpdateModal}
        closeModal={() => setShowTrxStatusUpdateModal(false)}
        transactions={selectedTransactions}
        investigationToken={investigationToken}
        setSelectedTransactions={setSelectedTransactions}
        trxTableState={tableState}
        setTrxTableState={setTableState}
      />
      {canAddEvidence && (
        <EvidenceModal
          isOpen={showEvidenceModal}
          closeModal={() => {
            setShowEvidenceModal(false);
            setSelectedTransactions([]);
            setTableState(prev => {
              const newState = {
                ...prev,
                rowSelection: {},
              };
              saveStateToLocalStorage(newState);
              return newState;
            });
          }}
          sourceTokens={selectedTransactions
            .map(trx => trx.external_transaction_identifier)
            .filter(Boolean)
            .map(String)}
          sourceType={EVIDENCE_DATA_TYPES.TRANSACTION}
          sourceComponent={(
            <Table
              columns={TRX_COLUMNS.filter(
                col => canReadEvidence || col.id !== 'evidence_count',
              )}
              data={selectedTransactions}
              onTableStateChange={newState => {
                setTableState(newState);
              }}
              state={tableState}
            />
          )}
          contextToken={investigationToken || entityToken}
        />
      )}
    </>
  );
};
